import { EditModalProps } from 'views/BootGuide/components/EditModal';
import { Active } from './PolicyPreview';
import useSmartField from 'hooks/useSmartField';
import {
  FV,
  getPlatformsByStores,
  getPolicyContentItems,
  getPolicyContentVoucher,
  newScenarioTypes,
  useGetTitle,
} from './share';
import styled from '@emotion/styled';
import { useEffect, useMemo, useState } from 'react';
import { difference } from 'lodash-es';
import { platformLabelMap } from 'services/config/platform';
import toLowerCase from 'utils/toLowerCase';
import { Button, ButtonProps, Divider, Switch, Tabs, Tooltip } from 'antd';
import { useMemoizedFn } from 'ahooks';
import produce from 'immer';
import Icon, {
  ExclamationCircleFilled,
  EyeOutlined,
  FormOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { ReactComponent as RegenerateSvg } from '../../assets/regenerate.svg';
import { ChunkRender } from 'views/BootGuide/components/Reuse/ChunkRender';
import { getChunks } from 'views/Aide/Scenario/PolicyTable';
import Translate from './Translate';
import { SSERender } from 'views/BootGuide/components/SSERender';
import { ItemPreview } from 'views/Aide/Scenario/NewPolicy/ContentItemListField';
import { Preview } from '../share';
import SafeImage from 'components/SafeImage';
import { css } from '@emotion/css';
import { regionLanguageMap } from 'services/config/region';
import posthog from '@leyan/lytics';
import { VoucherDisplay } from '@knowledge/Activity/ActivityForm/VoucherConfig/VoucherDisplay';
import { useFormikContext } from 'formik';

export type OnEditUpdate = (updateInfo: {
  value: string;
  scenarioType: Knowledge.Policy.ScenarioType;
  activitySendPoint: Knowledge.Policy.ActivitySendPoint;
  region: General.UpperCaseRegion;
  platformPolicy: Knowledge.Policy.PlatformPolicy;
}) => Promise<any>;

interface ItemHighlightPolicyListProps {
  activity: Knowledge.Activity.Detail;
  disabled?: boolean;
  autoTranslate?: boolean;
  simplified?: boolean;
  allPlatforms?: General.PlatformUpperCase[];
  activeRegion?: General.UpperCaseRegion;
  index1: number;
  index2: number;
  index3s: number[];
  onEdit: (meta: EditModalProps<string>) => void;
  onPreview: (active: Active) => void;
  onEditUpdate?: OnEditUpdate;
  onCancelEdit: () => void;
}

function ItemHighlightPolicyList(props: ItemHighlightPolicyListProps) {
  const {
    index1,
    index2,
    index3s,
    activeRegion,
    activity,
    allPlatforms: allPlatformsProp,
    disabled,
    simplified,
    autoTranslate,
    onPreview,
    onEdit,
    onEditUpdate,
    onCancelEdit,
  } = props;

  const { id: activityId, activityName, activityType, stores, language } = activity;

  const { t, getTitle } = useGetTitle();
  const { getTitle: getCnTitle } = useGetTitle('zh-CN');

  const [activeIndex, setActiveIndex] = useState(index3s[index1] ?? index3s[index3s.length - 1]);
  const [errorMap, setErrorMap] = useState<Record<number, boolean>>({});

  const {
    field: { value: contexts, onChange },
  } = useSmartField<FV['contexts']>('contexts');

  const {
    [index1]: {
      activitySendPoint,
      scenarioType,
      regionPolicyList: {
        [index2]: { region, platformPolicyList },
      },
    },
  } = contexts;
  const { [activeIndex]: activePolicy } = platformPolicyList;
  const {
    enable,
    platforms,
    itemHighlight,
    applyItems,
    executeContext: { content },
  } = activePolicy;

  const onEnableChange = useMemoizedFn((enable: boolean) => {
    onChange(
      produce(contexts, (draft) => {
        draft[index1].regionPolicyList[index2].platformPolicyList.forEach((policy, index) => {
          if (index3s.includes(index)) {
            policy.enable = enable;
          }
        });
        if (
          newScenarioTypes.includes(scenarioType) &&
          (activitySendPoint === 'EFFECTIVE_IN_QUANTUM' ||
            activitySendPoint === 'EFFECTIVE_NOT_IN_QUANTUM')
        ) {
          draft.forEach((context) => {
            if (
              context.scenarioType === scenarioType &&
              (context.activitySendPoint === 'EFFECTIVE_IN_QUANTUM' ||
                context.activitySendPoint === 'EFFECTIVE_NOT_IN_QUANTUM')
            ) {
              context.regionPolicyList[index2].platformPolicyList.forEach((policy) => {
                policy.enable = enable;
              });
            }
          });
        }
      }),
    );
  });
  const onContentChange = useMemoizedFn((index: number, content: string) =>
    onChange(
      produce(contexts, (draft) => {
        draft[index1].regionPolicyList[index2].platformPolicyList[index].executeContext.content =
          content;
      }),
    ),
  );

  const showPlatform = useMemo(() => {
    const allPlatforms = allPlatformsProp || getPlatformsByStores(stores);
    return difference(allPlatforms, platforms).length > 0;
  }, [stores, platforms, allPlatformsProp]);

  const getPlatform = (platforms: General.PlatformUpperCase[]) =>
    platforms.length === 1 ? platforms[0] : undefined;

  const platform = getPlatform(platforms);

  const policyContentItems = useMemo(
    () =>
      region &&
      getPolicyContentItems(
        activity,
        scenarioType,
        region,
        activitySendPoint,
        platform,
        applyItems,
      ),
    [activity, scenarioType, platform, region, activitySendPoint, applyItems],
  );

  const path = '/overseaim-oms/v1/config/gpt/knowledge/sse';

  const getQuery = (platform?: General.PlatformUpperCase, itemHighlight?: string) => ({
    knowledgeId: `${activity.id}`,
    promotionType: {
      NEW_PRODUCT_ONSALE_ACTIVITY: 'NEW_PRODUCT_ONSALE_ACTIVITY',
      CLEARANCE_SALE_ACTIVITY: 'CLEARANCE_SALE_ACTIVITY',
      RECOMMEND_HOT_PRODUCT_ACTIVITY: 'RECOMMEND_HOT_PRODUCT_ACTIVITY',
      DISCOUNT_ACTIVITY: 'PRODUCT_DISCOUNT',
      RECOMMEND_ACTIVITY: 'RECOMMEND_HOT_GOODS',
      FLASH_ACTIVITY: 'FLASH_SALE',
      VOUCHER_GIFT_ACTIVITY: 'VOUCHER_GIFT_ACTIVITY',
      MULTI_PACK_DISCOUNT_ACTIVITY: 'MULTI_PACK_DISCOUNT_ACTIVITY',
      LIVE_STREAMING_ACTIVITY: 'LIVE_STREAMING_ACTIVITY',
    }[activityType],
    scenarioType: `${
      activitySendPoint === 'WARM_UP' ? activitySendPoint : 'VALID_PERIOD'
    }_${scenarioType}`,
    region: region,
    ...(platform ? { platform } : {}),
    ...(itemHighlight ? { itemHighlight } : {}),
    ...(language ? { lang: language } : {}),
    ...(activitySendPoint === 'EFFECTIVE_IN_QUANTUM' ? { liveStage: 'DURING_LIVE' } : {}),
    ...(activitySendPoint === 'EFFECTIVE_NOT_IN_QUANTUM' ? { liveStage: 'BEFORE_LIVE' } : {}),
  });

  const hide = Boolean(activeRegion && region !== activeRegion);

  const buttonProps: ButtonProps = {
    type: 'text',
    size: 'small',
  };

  const previewButtonProps: ButtonProps = {
    ...buttonProps,
    icon: <EyeOutlined />,
    onClick: () =>
      onPreview({
        activitySendPoint,
        scenarioType,
        region,
        platforms,
        itemHighlight: platformPolicyList[index3s[activeIndex]].itemHighlight,
      }),
  };
  const previewButton = simplified ? (
    <Tooltip title={t('view_effect')}>
      <Button {...previewButtonProps} />
    </Tooltip>
  ) : (
    <Button {...previewButtonProps}>{t('view_effect')}</Button>
  );

  const regenerateButtonProps: ButtonProps = {
    ...buttonProps,
    icon: <Icon component={RegenerateSvg} />,
    onClick: () => onContentChange(activeIndex, ''),
  };
  const regenerateButton = disabled ? null : simplified ? (
    <Tooltip title={t('gpt_renew_content')}>
      <Button {...regenerateButtonProps} />
    </Tooltip>
  ) : (
    <Button {...regenerateButtonProps}>{t('gpt_renew_content')}</Button>
  );

  const encodeEmoji = (text: string) =>
    text.replaceAll(/\p{Emoji}+/gu, (emoji) => encodeURIComponent(emoji));
  const logEditContent = (value: string) => {
    posthog.capture('$editActivityContent', {
      activityId,
      activityName,
      activityTitle: getCnTitle(activitySendPoint, scenarioType, activity),
      preContent: encodeEmoji(decodeURIComponent(content)),
      nextContent: encodeEmoji(decodeURIComponent(value)),
    });
  };

  const editButtonProps: ButtonProps = {
    ...buttonProps,
    icon: <FormOutlined />,
    onClick: () =>
      onEdit({
        value: content,
        onChange: (value) => {
          logEditContent(value);
          onContentChange(activeIndex, value);
        },
        onSbChange:
          onEditUpdate &&
          ((value) => {
            logEditContent(value);
            return onEditUpdate({
              value,
              scenarioType,
              activitySendPoint,
              region,
              platformPolicy: activePolicy,
            }).then(() => onCancelEdit());
          }),
        platform,
        region,
        scenarioType: scenarioType as any,
        extraConcepts:
          activityType === 'LIVE_STREAMING_ACTIVITY'
            ? [
                {
                  id: '直播时间段',
                  label: '直播时间段',
                },
              ]
            : [
                {
                  id: '商品折扣',
                  label: '商品折扣',
                },
              ],
        path,
        query: getQuery(platform, itemHighlight),
        isActivity: true,
        items: policyContentItems,
      }),
  };
  const editButton = disabled ? null : simplified ? (
    <Tooltip title={t('marketing.编辑')}>
      <Button {...editButtonProps} />
    </Tooltip>
  ) : (
    <Button {...editButtonProps}>{t('marketing.编辑')}</Button>
  );

  const { initialValues } = useFormikContext<FV>();
  const initialEnable =
    initialValues.contexts[index1].regionPolicyList[index2].platformPolicyList[activeIndex].enable;
  const [generateContent, setGenerateContent] = useState(Boolean(initialEnable || content));
  useEffect(() => {
    if (enable) {
      setGenerateContent(true);
    }
  }, [enable]);

  return (
    <Container style={hide ? { display: 'none' } : undefined}>
      <header>
        <h4>
          {getTitle(activitySendPoint, scenarioType, activity)}
          {showPlatform &&
            `-${platforms.map((platform) => platformLabelMap[toLowerCase(platform)]).join('/')}`}
          <Switch disabled={disabled} size="small" checked={enable} onChange={onEnableChange} />
        </h4>
        {content ? (
          <div>
            {previewButton}
            {regenerateButton}
            {editButton}
          </div>
        ) : null}
      </header>
      <div>
        <Tabs
          size="small"
          activeKey={String(activeIndex)}
          onChange={(activeKey) => setActiveIndex(Number(activeKey))}
          className={index3s.length <= 1 || !generateContent ? styles.hideTab : undefined}
        >
          {index3s.map((index) => {
            const { [index]: policy } = platformPolicyList;
            const { [index]: error = false } = errorMap;
            const {
              platforms,
              executeContext: { content },
              itemHighlight,
              applyItems = [],
            } = policy;
            const platform = platforms.length === 1 ? platforms[0] : undefined;
            const policyContentItems = getPolicyContentItems(
              activity,
              scenarioType,
              region,
              activitySendPoint,
              platform,
              applyItems,
            );
            const voucher = getPolicyContentVoucher(activity, scenarioType);
            const hasItem = Boolean(policyContentItems?.length);
            const hasVoucher = !!voucher;
            const title = itemHighlight || t('未配置亮点商品');
            const tab = (
              <TabItem>
                <Tooltip title={title}>
                  <div>{title}</div>
                </Tooltip>
                {error ? <ExclamationCircleFilled /> : content ? null : <LoadingOutlined />}
                {itemHighlight && applyItems.length ? (
                  <Preview
                    content={<ItemPreview ids={applyItems.map(({ itemId }) => String(itemId))} />}
                  />
                ) : null}
              </TabItem>
            );

            if (!generateContent) {
              return (
                <Tabs.TabPane key={index} tab={tab}>
                  <div style={{ color: 'var(--fett-text-tertiary)' }}>
                    {t('关闭后将不再生成此时机的专属销售方案，如需生成内容请开启')}
                  </div>
                </Tabs.TabPane>
              );
            }

            return (
              <Tabs.TabPane key={index} tab={tab} forceRender>
                {content ? (
                  [
                    <ChunkRender
                      chunks={getChunks({ content, translatedContent: '' })}
                      platform={platform || 'SHOPEE'}
                      region={region}
                      language={regionLanguageMap[toLowerCase(region)]}
                      item={
                        policyContentItems?.length ? (
                          <Item>
                            <SafeImage preview src={policyContentItems[0].imageUrl} width={64} />
                            <section>
                              <div>{policyContentItems[0].title}</div>
                              <span>{t('随机发送已选推荐商品')}</span>
                            </section>
                          </Item>
                        ) : null
                      }
                      voucher={hasVoucher && <VoucherDisplay data={voucher!} border />}
                    />,
                    autoTranslate && [
                      <Divider type="horizontal" />,
                      <Translate value={content} platform={platform || 'SHOPEE'} region={region} />,
                    ],
                  ]
                ) : (
                  <SSERender
                    path={path}
                    query={getQuery(platform, itemHighlight)}
                    onDone={(content) => onContentChange(index, content)}
                    chunkProps={{
                      platform: platform || 'SHOPEE',
                      region,
                    }}
                    hasItem={hasItem}
                    hasVoucher={hasVoucher}
                    retry={3}
                    onSSEStatusChange={(status) =>
                      setErrorMap((map) => ({ ...map, [index]: status === 'Error' }))
                    }
                  />
                )}
              </Tabs.TabPane>
            );
          })}
        </Tabs>
      </div>
    </Container>
  );
}

const Container = styled.div`
  margin-bottom: 12px;
  border: solid 1px var(--fett-border-default);
  border-radius: 2px;
  > header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 48px;
    padding: 0 12px;
    background: var(--fett-bg-info-tip);
    > h4 {
      flex: 1 0 0;
      margin-bottom: 0;
      .ant-switch {
        margin-left: 12px;
      }
    }
    > div {
      flex: 0 0 auto;
      > .ant-btn-icon-only {
        margin-left: 12px;
      }
    }
  }
  > div {
    padding: 12px;
    > .ant-tabs {
      .ant-tabs-nav {
        margin-bottom: 12px;
      }
      .ant-tabs-tabpane {
        .ant-divider {
          margin: 12px 0;
        }
      }
    }
  }
`;

const TabItem = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 400;
  > div {
    max-width: 180px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .anticon {
    margin: 0 4px;
  }
  .anticon-exclamation-circle {
    color: var(--fett-surface-error-text);
  }
  .anticon-spin {
    color: var(--fett-text-primary);
  }
`;

const Item = styled.div`
  display: flex;
  width: 238px;
  padding: 4px;
  background: var(--fett-erp-card-bg);
  border-radius: 2px;
  > div {
    flex: 0 0 auto;
    margin-right: 8px;
  }
  > section {
    flex: 1 0 0;
    overflow: hidden;
    > div {
      width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    > span {
      font-size: 12px;
      color: var(--fett-scenario-success-text);
    }
  }
`;

const styles = {
  hideTab: css`
    .ant-tabs-nav {
      display: none;
    }
  `,
};

export default ItemHighlightPolicyList;
